import React from 'react'

import { toJS } from 'mobx'

import {
  IUploadLogAggregationFilterInput,
  UploadLogFilterType,
} from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { UNASSIGNED_FILTER_OPTION } from '~/client/src/shared/components/Deliveries/DeliveriesView.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { getIdFromLocation } from '~/client/src/shared/utils/logisticsHelper'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

type MapFn<T> = (key: string) => T

function isUnassignedValue(value: string): boolean {
  return value === UNASSIGNED || value === UNASSIGNED_FILTER_OPTION
}

function extractFilterKeys<T extends string | null>(
  rawKeys: string[],
  mapFn: MapFn<T> = key => key as unknown as T,
): T[] {
  const keys = [...rawKeys]
  for (let i = keys.length - 1; i >= 0; i -= 1) {
    if (isUnassignedValue(keys[i])) {
      keys.splice(i, 1)
    }
  }
  return keys.map(mapFn)
}

type FilterConfigEntry<T> = {
  key: string
  filterType: UploadLogFilterType
  mapFn?: MapFn<T>
  secondaryKey?: string
  needsConversion?: boolean
}

enum FileConfigKeys {
  companyIds = 'companyIds',
  locationIds = 'locationIds',
  fileCategories = 'fileCategories',
  fileTypes = 'fileTypes',
  userIds = 'userIds',
  workflowCategoryIds = 'workflowCategoryIds',
  workflowTypeIds = 'workflowTypeIds',
}

const filterConfig: FilterConfigEntry<any>[] = [
  {
    key: FileConfigKeys.companyIds,
    filterType: UploadLogFilterType.Company,
    mapFn: key => key,
  },
  {
    key: FileConfigKeys.locationIds,
    filterType: UploadLogFilterType.Location,
    mapFn: getIdFromLocation,
  },
  {
    key: FileConfigKeys.fileCategories,
    filterType: UploadLogFilterType.Filetype,
    mapFn: key => key,
  },
  {
    key: FileConfigKeys.fileTypes,
    filterType: UploadLogFilterType.FileExtension,
    mapFn: key => key,
  },
  {
    key: FileConfigKeys.userIds,
    filterType: UploadLogFilterType.UploadedBy,
    mapFn: key => key,
  },
  {
    key: FileConfigKeys.workflowCategoryIds,
    filterType: UploadLogFilterType.WorkflowCategory,
    mapFn: key => key,
  },
  {
    key: FileConfigKeys.workflowTypeIds,
    filterType: UploadLogFilterType.Formtype,
    mapFn: key => key,
    needsConversion: true,
  },
]

function convertWorkflowTypeNamesToIds(
  displayNames: string[],
  workflowTypesStore: WorkflowTypesStore,
): string[] {
  if (!workflowTypesStore) {
    console.warn('PermitTypesStore not available for workflow type conversion')
    return displayNames
  }

  const typeIds: string[] = []
  displayNames.forEach(displayName => {
    if (displayName === UNASSIGNED_FILTER_OPTION) {
      typeIds.push(displayName)
      return
    }

    const matchingTypes = workflowTypesStore.allTypes.filter(
      (type: any) => type.name === displayName,
    )

    if (matchingTypes.length > 0) {
      matchingTypes.forEach((type: any) => typeIds.push(type.id))
    } else {
      typeIds.push(displayName)
    }
  })

  return typeIds
}

export default function useUploadLogsFilter(
  appState: DesktopInitialState,
  workflowTypesStore: WorkflowTypesStore,
): IUploadLogAggregationFilterInput {
  return React.useMemo(() => {
    const fieldsMap = appState.attachmentsFilters?.fieldsMap || {}

    const filters = filterConfig.reduce(
      (acc, { key, filterType, mapFn, needsConversion }) => {
        const filterData = fieldsMap[filterType]
        if (filterData?.initialFilterOptions) {
          const rawKeys = Array.from(
            filterData.initialFilterOptions.keys() || [],
          ) as string[]

          const hasUnassigned = rawKeys.some(isUnassignedValue)
          switch (filterType) {
            case UploadLogFilterType.Company:
              if (hasUnassigned) acc.includeUnassignedCompanies = true
              break
            case UploadLogFilterType.Location:
              if (hasUnassigned) acc.includeUnassignedLocations = true
              break
            case UploadLogFilterType.UploadedBy:
              if (hasUnassigned) acc.includeUnassignedUsers = true
              break
            case UploadLogFilterType.WorkflowCategory:
              if (hasUnassigned) acc.includeUnassignedWorkflowCategories = true
              break
            case UploadLogFilterType.Formtype:
              if (hasUnassigned) acc.includeUnassignedWorkflowTypes = true
              break
            default:
              break
          }

          let filterValues = extractFilterKeys(rawKeys, mapFn)

          if (needsConversion && filterType === UploadLogFilterType.Formtype) {
            filterValues = convertWorkflowTypeNamesToIds(
              filterValues,
              workflowTypesStore,
            )
          }

          acc[key] = filterValues
        }
        return acc
      },
      {} as Record<string, any>,
    )

    filters.searchString = appState.attachmentsFilters?.searchKey
    filters.includeHidden = false

    return filters
  }, [toJS(appState.attachmentsFilters?.fieldsMap)])
}
